import type ChartViewType from 'echarts/lib/view/Chart';
import ChartView from 'echarts/lib/view/Chart';
import { DvRadarSeriesDataItemOption, DvRadarSeriesModelClazz } from './radarSeriesModel';
import type GlobalModel from 'echarts/lib/model/Global';
import type ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import * as graphic from 'echarts/lib/util/graphic';
import { normalizeSymbolSize } from 'echarts/lib/util/symbol';
import SeriesData from 'echarts/lib/data/SeriesData';
import { VectorArray } from 'zrender/src/core/vector';

import { setStatesStylesFromModel, toggleHoverEmphasis } from 'echarts/lib/util/states';
import * as zrUtil from 'zrender/src/core/util';
import * as symbolUtil from 'echarts/lib/util/symbol';
import { ColorString, ViewRootGroup } from 'echarts/lib/util/types';
import { setLabelStyle, getLabelStatesModels } from 'echarts/lib/label/labelStyle';
import { saveOldStyle } from 'echarts/lib/animation/basicTrasition';

export type dvRadarSymbol = ReturnType<typeof symbolUtil.createSymbol> & {
  __dimIdx: number;
};

const transparentColor = 'rgba(0,0,0,0)';

export type DvRadarViewClazz = DvRadarView & ChartViewType;

class DvRadarView extends ChartView {
  static readonly type = 'dvRadar';

  type = DvRadarView.type;

  private _data: SeriesData<DvRadarViewClazz>;

  _exampleGroup: graphic.Group;

  _activeIndex: number | undefined;

  public radarPointer: graphic.Line;

  init() {
    /* TODO document why this method 'init' is empty */
  }

  render(
    this: DvRadarViewClazz,
    seriesModel: DvRadarSeriesModelClazz,
    ecModel: GlobalModel,
    api: ExtensionAPI
  ) {
    const group = this.group;

    const data = seriesModel.getData();
    const oldData = this._data;

    // 添加扇形选中态
    const indicatorGroup = new graphic.Group();

    data
      .diff(oldData)
      .add(function (idx) {
        let points = data.getItemLayout(idx);

        const smoothValue = seriesModel.option.smooth || 0;
        if (smoothValue == 0) {
          points.push(points[0]);
        }
        if (!points) {
          return;
        }
        const polygon = new graphic.Polygon();
        const polyline = new graphic.Polyline();
        const target = {
          shape: {
            points: points,
            smooth: smoothValue
          }
        };
        polygon.shape.points = getInitialPoints(seriesModel, points);
        polyline.shape.points = getInitialPoints(seriesModel, points);
        graphic.initProps(polygon, target, seriesModel, idx);
        graphic.initProps(polyline, target, seriesModel, idx);
        const itemGroup = new graphic.Group();
        const symbolGroup = new graphic.Group();
        itemGroup.add(polyline);
        itemGroup.add(polygon);
        itemGroup.add(symbolGroup);
        updateSymbols(seriesModel, polyline.shape.points, points, symbolGroup, data, idx, true);
        data.setItemGraphicEl(idx, itemGroup);
      })
      .update(function (newIdx, oldIdx) {
        const itemGroup = oldData.getItemGraphicEl(oldIdx) as graphic.Group;

        const polyline = itemGroup.childAt(0) as graphic.Polyline;
        const polygon = itemGroup.childAt(1) as graphic.Polygon;
        const symbolGroup = itemGroup.childAt(2) as graphic.Group;
        let points = data.getItemLayout(newIdx);
        const itemModel = data.getItemModel<DvRadarSeriesDataItemOption>(newIdx);
        const smoothValue = itemModel.option.smooth || 0;
        if (smoothValue == 0) {
          points.push(points[0]);
        }
        const target = {
          shape: {
            points: points,
            smooth: smoothValue
          }
        };

        if (!target.shape.points) {
          return;
        }

        updateSymbols(
          seriesModel,
          polyline.shape.points,
          target.shape.points,
          symbolGroup,
          data,
          newIdx,
          false
        );

        saveOldStyle(polygon);
        saveOldStyle(polyline);
        graphic.updateProps(polygon, target, seriesModel);

        data.setItemGraphicEl(newIdx, itemGroup);
      })
      .remove(function (idx) {
        group.remove(oldData.getItemGraphicEl(idx));
      })
      .execute();

    data.eachItemGraphicEl(function (itemGroup: graphic.Group, idx) {
      const itemModel = data.getItemModel<DvRadarSeriesDataItemOption>(idx);
      const polyline = itemGroup.childAt(0) as graphic.Polyline;
      const polygon = itemGroup.childAt(1) as graphic.Polygon;
      const symbolGroup = itemGroup.childAt(2) as graphic.Group;
      // Radar uses the visual encoded from itemStyle.
      const itemStyle = data.getItemVisual(idx, 'style');
      const color = itemStyle.fill;

      group.add(itemGroup);

      polyline.useStyle(
        zrUtil.defaults(itemModel.getModel('lineStyle').getLineStyle(), {
          fill: 'none',
          stroke: color
        })
      );

      setStatesStylesFromModel(polyline, itemModel, 'lineStyle');
      setStatesStylesFromModel(polygon, itemModel, 'areaStyle');

      const areaStyleModel = itemModel.getModel('areaStyle');
      const polygonIgnore = areaStyleModel.isEmpty() && areaStyleModel.parentModel.isEmpty();

      polygon.ignore = polygonIgnore;

      zrUtil.each(['emphasis', 'select', 'blur'] as const, function (stateName) {
        const stateModel = itemModel.getModel([stateName, 'areaStyle']);
        const stateIgnore = stateModel.isEmpty() && stateModel.parentModel.isEmpty();
        // Won't be ignore if normal state is not ignore.
        polygon.ensureState(stateName).ignore = stateIgnore && polygonIgnore;
      });
      polygon.useStyle(
        zrUtil.defaults(areaStyleModel.getAreaStyle(), {
          fill: color,
          opacity: 0.7,
          decal: itemStyle.decal,
          stroke: color,
          strokeNoScale: false
        })
      );

      const emphasisModel = itemModel.getModel('emphasis');
      const itemHoverStyle = emphasisModel.getModel('itemStyle').getItemStyle();
      symbolGroup.eachChild(function (symbolPath: dvRadarSymbol, index: number) {
        symbolPath.useStyle(itemStyle);
        symbolPath.setColor(color);
        symbolPath.style.strokeNoScale = true;

        const pathEmphasisState = symbolPath.ensureState('emphasis');
        pathEmphasisState.style = zrUtil.clone(itemHoverStyle);
        let defaultText = data.getStore().get(data.getDimensionIndex(symbolPath.__dimIdx), idx);
        (defaultText == null || isNaN(defaultText as number)) && (defaultText = '');

        setLabelStyle(symbolPath, getLabelStatesModels(itemModel), {
          labelFetcher: data.hostModel,
          labelDataIndex: idx,
          labelDimIndex: symbolPath.__dimIdx,
          defaultText: defaultText as string,
          inheritColor: color as ColorString,
          defaultOpacity: itemStyle.opacity
        });
      });

      toggleHoverEmphasis(
        itemGroup,
        emphasisModel.get('focus'),
        emphasisModel.get('blurScope'),
        emphasisModel.get('disabled')
      );
    });

    this._data = data;

    let elId = 0;
    data.eachItemGraphicEl(function () {
      elId += 1;
    });
    addSectorSelector(this.radarPointer, group, seriesModel, indicatorGroup, elId, api);
  }

  remove(this: DvRadarViewClazz, ecModel: GlobalModel, api: ExtensionAPI) {
    // 处理对象销毁
    this.group.removeAll();
    this._data = null;
    this.radarPointer = null;
  }
}

function createSymbol(data: SeriesData<DvRadarSeriesModelClazz>, idx: number) {
  const symbolType = data.getItemVisual(idx, 'symbol') || 'circle';
  if (symbolType === 'none') {
    return;
  }
  const symbolSize = normalizeSymbolSize(data.getItemVisual(idx, 'symbolSize'));
  const symbolPath = symbolUtil.createSymbol(symbolType, -1, -1, 2, 2);
  const symbolRotate = data.getItemVisual(idx, 'symbolRotate') || 0;
  symbolPath.attr({
    style: {
      strokeNoScale: false
    },
    z2: 5,
    scaleX: symbolSize[0] / 2,
    scaleY: symbolSize[1] / 2,
    rotation: (symbolRotate * Math.PI) / 180 || 0
  });
  return symbolPath as dvRadarSymbol;
}

function updateSymbols(
  seriesModel: DvRadarSeriesModelClazz,
  oldPoints: VectorArray[],
  newPoints: VectorArray[],
  symbolGroup: graphic.Group,
  data: SeriesData<DvRadarSeriesModelClazz>,
  idx: number,
  isInit?: boolean
) {
  // Simply rerender all
  symbolGroup.removeAll();
  for (let i = 0; i < newPoints.length - 1; i++) {
    const symbolPath = createSymbol(data, idx);
    if (symbolPath) {
      symbolPath.__dimIdx = i;
      if (oldPoints[i]) {
        symbolPath.setPosition(oldPoints[i]);
        graphic[isInit ? 'initProps' : 'updateProps'](
          symbolPath,
          {
            x: newPoints[i][0],
            y: newPoints[i][1]
          },
          seriesModel,
          idx
        );
      } else {
        symbolPath.setPosition(newPoints[i]);
      }
      symbolGroup.add(symbolPath);
      // }
    }
  }
}

function getInitialPoints(seriesModel: DvRadarSeriesModelClazz, points: number[][]) {
  const radarSys = seriesModel.coordinateSystem;
  return zrUtil.map(points, function (pt) {
    return [radarSys.cx, radarSys.cy];
  });
}

// 添加扇形选择器
function addSectorSelector(
  radarPointer: graphic.Line,
  group: ViewRootGroup,
  seriesModel: DvRadarSeriesModelClazz,
  indicatorGroup: graphic.Group,
  elId: number,
  api: ExtensionAPI
) {
  const sectorSelector = seriesModel.option.sectorSelector;

  if (!sectorSelector) {
    return;
  }

  if (sectorSelector.show === undefined) {
    sectorSelector.show = true;
  }

  if (sectorSelector.show === true) {
    // 确定轴的数量
    const dimensionNumber = seriesModel.coordinateSystem.dimensions.length;
    // 计算每个扇形的角度
    const angleRange = (Math.PI * 2) / dimensionNumber;

    let startAngle = (-2 * Math.PI) / 3;
    let endAngel = startAngle + angleRange;
    let sectorStartAngle = (-1 / 2) * Math.PI - (1 / 2) * angleRange;
    const x = seriesModel.coordinateSystem.cx;
    const y = seriesModel.coordinateSystem.cy;
    const r = seriesModel.coordinateSystem.r;

    if (sectorSelector.lineStyle) {
      // 绘制axisPointer线条
      radarPointer = new graphic.Line({
        name: 'radarPointer',
        shape: {
          x1: x,
          y1: y,
          x2: x,
          y2: y
        },
        style: sectorSelector.lineStyle,
        zlevel: 4
      });
      indicatorGroup.add(radarPointer); // 添加新的线条
    }

    // 给每个轴绘制扇形
    for (let i = 0; i < dimensionNumber; i++) {
      const sectorEndAngle = sectorStartAngle + angleRange;
      const sector = new graphic.Sector({
        hover_id: i,
        shape: {
          cx: x,
          cy: y,
          r: r,
          r0: 0, // 内半径，要绘制扇形而非圆环，设置为0
          startAngle: sectorStartAngle,
          endAngle: sectorEndAngle,
          zlevel: 10
        },
        style: {
          fill: transparentColor // 扇形的初始填充颜色为透明
        }
      });

      startAngle = endAngel;
      endAngel = startAngle + (Math.PI * 2) / dimensionNumber;
      sectorStartAngle = sectorEndAngle;
      let linePointer = radarPointer;
      // 为扇形添加鼠标悬停事件代理
      sector.on('mouseover', event => {
        api.dispatchAction({
          type: 'radarMouseOverSector',
          event: event,
          line: linePointer,
          group: group
        });
      });

      // 为扇形添加鼠标移出事件代理
      sector.on('mouseout', event => {
        api.dispatchAction({
          type: 'radarMouseOutSector',
          event: event,
          line: linePointer,
          group: group
        });
      });
      const data = seriesModel.getData();
      data.setItemGraphicEl(elId + i, sector);
      indicatorGroup.add(sector);
    }
    group.add(indicatorGroup);
  }
}

export default DvRadarView;
